import type { ReactNode } from 'react';
import { Box, Card, CardActionArea, CardContent, LinearProgress, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import type { BillingSummary } from '../../lib/ops-api';
import type { BillingStory, MonthTrend } from '../../lib/billing-insights';
import { formatRupiah } from '../../lib/format';
import { useT } from '../../i18n';
import { chartColors } from '../../theme';

const rp = (cents: number) => `Rp ${formatRupiah(cents)}`;

function KpiCard({
  label,
  value,
  sub,
  footer,
  onClick,
  tone,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  footer?: ReactNode;
  onClick?: () => void;
  tone?: 'error';
}) {
  const body = (
    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography
        variant="h5"
        fontWeight={600}
        sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums', color: tone === 'error' ? 'error.main' : 'text.primary' }}
      >
        {value}
      </Typography>
      <Box sx={{ minHeight: 22 }}>
        {sub ? <Typography variant="caption" color="text.secondary">{sub}</Typography> : null}
      </Box>
      {footer ? <Box sx={{ mt: 'auto', pt: 1.5 }}>{footer}</Box> : null}
    </CardContent>
  );
  return (
    <Card
      variant="outlined"
      sx={{ transition: 'box-shadow 200ms ease, transform 200ms ease', ...(onClick ? { '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } } : {}) }}
    >
      {onClick ? <CardActionArea onClick={onClick} sx={{ height: '100%' }}>{body}</CardActionArea> : body}
    </Card>
  );
}

export function KpiCards({
  summary,
  trend,
  story,
  target,
  onShowUnits,
  onShowArrears,
}: {
  summary: BillingSummary;
  /** Monthly history for the collected sparkline. */
  trend: MonthTrend[] | null;
  story: BillingStory | null;
  target: number;
  onShowUnits: () => void;
  onShowArrears: () => void;
}) {
  const t = useT();
  const theme = useTheme();
  const pct = Math.round(summary.collectionRate * 100);
  const unpaidHouses = summary.unitsBilled - summary.unitsPaid;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
      <KpiCard
        label={t('rpt.kpiCollected')}
        value={rp(summary.collectedCents)}
        // Out of how much was billed: the sentence that used to sit above the cards.
        sub={t('rpt.kpiOfBilled', { billed: rp(summary.collectedCents + summary.outstandingCents) })}
        footer={
          trend && trend.length > 1 ? (
            <Tooltip title={t('rpt.kpiSparkHint')} placement="bottom">
              <Box>
                <SparkLineChart
                  data={trend.map((m) => m.collectedCents / 100)}
                  height={40}
                  area
                  curve="natural"
                  colors={[chartColors(theme.palette.mode).primary]}
                  showTooltip
                  showHighlight
                  xAxis={{ scaleType: 'point', data: trend.map((m) => m.month) }}
                  // Start near the lowest month, not zero, so the ups and downs show.
                  yAxis={{ min: Math.min(...trend.map((m) => m.collectedCents / 100)) * 0.85 }}
                  valueFormatter={(v) => (v === null ? '' : `Rp ${formatRupiah(v * 100)}`)}
                />
              </Box>
            </Tooltip>
          ) : null
        }
      />
      <KpiCard
        label={t('rpt.kpiRate')}
        value={`${pct}%`}
        sub={t('rpt.kpiTarget', { pct: Math.round(target * 100) })}
        footer={
          // The bar with the target as a tick, so "how far off" is visible at a glance.
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: pct >= target * 100 ? 'success.main' : 'secondary.main' },
              }}
            />
            <Tooltip title={t('rpt.kpiTarget', { pct: Math.round(target * 100) })}>
              <Box sx={{ position: 'absolute', top: -3, bottom: -3, left: `${target * 100}%`, width: 2, bgcolor: 'text.primary', borderRadius: 1 }} />
            </Tooltip>
          </Box>
        }
      />
      <KpiCard
        label={t('rpt.kpiHousesPaid')}
        value={
          <>
            {summary.unitsPaid}
            <Typography component="span" variant="body1" color="text.secondary">{' / '}{summary.unitsBilled}</Typography>
          </>
        }
        sub={unpaidHouses > 0 ? t('rpt.kpiNotYetPaid', { count: unpaidHouses }) : t('rpt.kpiAllPaid')}
        footer={<Typography variant="caption" color="primary">{t('rpt.kpiSeeUnits')} →</Typography>}
        onClick={onShowUnits}
      />
      <KpiCard
        label={t('rpt.kpiArrears')}
        tone={story && story.owedCents > 0 ? 'error' : undefined}
        value={story ? rp(story.owedCents) : '—'}
        sub={story ? (story.housesBehind > 0 ? t('rpt.kpiArrearsHouses', { count: story.housesBehind }) : t('rpt.kpiNoArrears')) : undefined}
        footer={story && story.housesBehind > 0 ? <Typography variant="caption" color="primary">{t('rpt.kpiSeeArrears')} →</Typography> : null}
        onClick={story && story.housesBehind > 0 ? onShowArrears : undefined}
      />
    </Box>
  );
}
